import type { Character } from "@cloudnest/horror-race-engine";
import { HorrorCharacterChip } from "./HorrorCharacterChip";

interface SecretStripProps {
  readonly secretSelection: readonly Character[];
  /**
   * Controlled reveal state. Lifted to GameScreen so the toggle survives the
   * phase-branch unmount at round/set settlement — an "on" strip stays on
   * across rounds and races instead of resetting to off.
   */
  readonly open: boolean;
  readonly onToggle: () => void;
}

/**
 * T15 (v2) — "내 선택" docked toggle strip at the top of the shared bottom
 * action surface (replaces the v1 floating FAB). The strip has a FIXED height,
 * so toggling never resizes the action area — the chips reveal width-only. The
 * chips are the same vertical `HorrorCharacterChip` used everywhere else
 * (unified token, no separate design). The icon swaps eye ⇄ eye-off
 * (reveal / hide). Honors `prefers-reduced-motion`.
 */
export function SecretStrip({
  secretSelection,
  open,
  onToggle,
}: SecretStripProps) {
  return (
    <div
      data-testid="horror-secret-strip"
      className="flex h-[54px] items-center justify-end gap-3 px-3"
      style={{ borderBottom: "1px solid var(--cn-horror-lane-divider)" }}
    >
      {/* Width-revealed chips — the FIXED strip height keeps the area constant. */}
      <div
        data-testid="my-secret-badge"
        className={`mr-auto flex items-center gap-3.5 overflow-hidden transition-all duration-300 motion-reduce:transition-none ${
          open ? "max-w-[360px] opacity-100" : "max-w-0 opacity-0"
        }`}
      >
        <span
          className="shrink-0 font-bold text-[10px]"
          style={{ color: "var(--cn-horror-p3)" }}
        >
          내 선택
        </span>
        {secretSelection.map((character) => (
          <HorrorCharacterChip
            key={character}
            character={character}
            iconSize={24}
            labelSize={10}
          />
        ))}
      </div>

      <button
        type="button"
        data-testid="horror-secret-toggle"
        aria-expanded={open}
        aria-label="내 선택 보기/숨기기"
        onClick={onToggle}
        className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 font-bold text-xs leading-none transition-colors motion-reduce:transition-none"
        style={{
          backgroundColor: "transparent",
          borderColor: open
            ? "var(--cn-horror-p3)"
            : "var(--cn-horror-surface-border)",
          color: open ? "var(--cn-horror-text)" : "var(--cn-horror-text-muted)",
        }}
      >
        <span
          className="relative inline-flex h-4 w-4 shrink-0"
          style={{ color: "var(--cn-horror-p3)" }}
        >
          {/* eye (reveal) — shown when collapsed */}
          <svg
            className={`absolute inset-0 transition-opacity duration-200 motion-reduce:transition-none ${
              open ? "opacity-0" : "opacity-100"
            }`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z"
              stroke="currentColor"
              strokeWidth="1.8"
            />
            <circle cx="12" cy="12" r="2.7" fill="currentColor" />
          </svg>
          {/* eye-off (hide) — shown when open */}
          <svg
            className={`absolute inset-0 transition-opacity duration-200 motion-reduce:transition-none ${
              open ? "opacity-100" : "opacity-0"
            }`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z"
              stroke="currentColor"
              strokeWidth="1.8"
            />
            <circle cx="12" cy="12" r="2.7" fill="currentColor" />
            <line
              x1="3"
              y1="3"
              x2="21"
              y2="21"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </span>
        <span>내 선택</span>
      </button>
    </div>
  );
}
